import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, from, Observable, of, shareReplay, switchMap, tap, throwError } from 'rxjs';

import { ApiEndpoints } from '../constants/api-endpoints';
import { environment } from '../../../environments/environment';
import { TokenStorageService } from './token-storage.service';
import { SubscriptionService } from './subscription.service';

export interface AttachmentRecord {
  id?: number;
  accountId?: number;
  featureType?: number;
  featureId?: number;
  attachmentFileName?: string;
  attachmentUrl?: string;
  fileSize?: number;
  status?: number;
}

@Injectable({ providedIn: 'root' })
export class AttachmentService {
  private readonly previewStoragePrefix = 'htally_attachment_preview_';
  private readonly baseUrl = environment.apiBaseUrl.replace(/\/+$/, '');
  private readonly previewUrlCache = new Map<number, string>();
  private readonly inFlightPreviewRequests = new Map<number, Observable<string>>();

  constructor(
    private readonly http: HttpClient,
    private readonly tokens: TokenStorageService,
    private readonly subscriptionService: SubscriptionService
  ) {}

  manageAttachment(payload: {
    featureType: number;
    featureId: number;
    actionType?: number;
    files?: File[];
    deletingIds?: number[];
    service?: string;
  }): Observable<AttachmentRecord[]> {
    const form = new FormData();
    const endpoint = ApiEndpoints.FILE_MANAGE_ATTACHMENT;
    const actionType = payload.actionType ?? endpoint.actionType;
    const service = `${payload.service ?? endpoint.service}`.trim();

    form.append('featureType', `${payload.featureType}`);
    form.append('featureId', `${payload.featureId}`);
    form.append('actionType', `${actionType}`);
    form.append('service', service);

    (payload.files ?? []).forEach((file) => form.append('file', file));
    (payload.deletingIds ?? []).forEach((id) => form.append('deleting_ids', `${id}`));

    return this.http.post<AttachmentRecord[]>(this.buildUrl(endpoint.apiPath), form, {
      headers: this.authHeaders()
    }).pipe(
      tap(() => this.subscriptionService.requestSummaryRefresh()),
      catchError((error) => throwError(() => new Error(this.extractErrorMessage(error))))
    );
  }

  previewUrlByAttachmentId(attachmentId: number): Observable<string> {
    const endpoint = ApiEndpoints.FILE_PREVIEW_URL_ATTACHMENT;
    return this.http.post(this.buildUrl(endpoint.apiPath), {
      attachmentId,
      service: endpoint.service,
      actionType: endpoint.actionType
    }, {
      headers: this.authHeaders().set('Content-Type', 'application/json'),
      responseType: 'text'
    });
  }

  previewUrlByAttachmentIdCached(attachmentId: number, forceRefresh = false): Observable<string> {
    if (!forceRefresh) {
      const cached = this.previewUrlCache.get(attachmentId);
      if (cached) {
        return of(cached);
      }
      const persisted = this.readPreviewUrlFromStorage(attachmentId);
      if (persisted) {
        this.previewUrlCache.set(attachmentId, persisted);
        return of(persisted);
      }
      const inFlight = this.inFlightPreviewRequests.get(attachmentId);
      if (inFlight) {
        return inFlight;
      }
    }

    const request$ = this.previewUrlByAttachmentId(attachmentId).pipe(
      switchMap((url) => from(this.resolveImageSource(url))),
      tap((resolved) => {
        const sanitized = `${resolved ?? ''}`.trim();
        if (sanitized) {
          this.previewUrlCache.set(attachmentId, sanitized);
          this.writePreviewUrlToStorage(attachmentId, sanitized);
        }
      }),
      tap({
        next: () => this.inFlightPreviewRequests.delete(attachmentId),
        error: () => this.inFlightPreviewRequests.delete(attachmentId)
      }),
      shareReplay(1)
    );

    this.inFlightPreviewRequests.set(attachmentId, request$);
    return request$;
  }

  clearPreviewUrlCache(attachmentId?: number): void {
    if (Number.isFinite(attachmentId as number)) {
      this.previewUrlCache.delete(Number(attachmentId));
      this.inFlightPreviewRequests.delete(Number(attachmentId));
      this.removePreviewUrlFromStorage(Number(attachmentId));
      return;
    }
    this.previewUrlCache.clear();
    this.inFlightPreviewRequests.clear();
    this.clearPreviewUrlStorage();
  }

  private async resolveImageSource(url: string): Promise<string> {
    const resolved = `${url ?? ''}`.trim();
    if (!resolved || resolved.startsWith('data:')) {
      return resolved;
    }

    try {
      const response = await fetch(resolved);
      if (!response.ok) {
        return resolved;
      }
      const blob = await response.blob();
      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(new Error('Failed to read image data.'));
        reader.readAsDataURL(blob);
      });
    } catch {
      return resolved;
    }
  }

  private buildUrl(endpoint: string): string {
    const sanitizedEndpoint = endpoint.replace(/^\/+/, '');
    return `${this.baseUrl}/${sanitizedEndpoint}`;
  }

  private authHeaders(): HttpHeaders {
    let headers = new HttpHeaders();
    const token = this.tokens.accessToken;
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  }

  private extractErrorMessage(error: any): string {
    const responseMessage = `${error?.error?.message ?? ''}`.trim();
    if (responseMessage) {
      return responseMessage;
    }

    const nestedValidationMessage = `${error?.error?.errors?.[0]?.message ?? ''}`.trim();
    if (nestedValidationMessage) {
      return nestedValidationMessage;
    }

    if (error?.status === 400) {
      return 'Validation failed.';
    }

    return `${error?.message ?? 'Attachment request failed.'}`.trim() || 'Attachment request failed.';
  }

  private readPreviewUrlFromStorage(attachmentId: number): string | null {
    const storage = this.getStorage();
    if (!storage) {
      return null;
    }
    const value = `${storage.getItem(this.storageKey(attachmentId)) ?? ''}`.trim();
    return value || null;
  }

  private writePreviewUrlToStorage(attachmentId: number, url: string): void {
    const storage = this.getStorage();
    if (!storage || !url) {
      return;
    }
    storage.setItem(this.storageKey(attachmentId), url);
  }

  private removePreviewUrlFromStorage(attachmentId: number): void {
    const storage = this.getStorage();
    if (!storage) {
      return;
    }
    storage.removeItem(this.storageKey(attachmentId));
  }

  private clearPreviewUrlStorage(): void {
    const storage = this.getStorage();
    if (!storage) {
      return;
    }
    const keysToRemove: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key && key.startsWith(this.previewStoragePrefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => storage.removeItem(key));
  }

  private storageKey(attachmentId: number): string {
    return `${this.previewStoragePrefix}${attachmentId}`;
  }

  private getStorage(): Storage | null {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  }
}
